import { LineChart } from '@tamagui/lucide-icons';
import { noop } from '@tanstack/react-query';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { QueryState } from '@unistocks/components/query-state/query-state';
import {
  DashboardItemFrame,
  DashboardItemProps
} from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item';
import { DashboardItemHeader } from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item-header';
import { usePortfolioHistory } from '@unistocks/features/portfolios/hooks/use-portfolio-history';
import { t } from '@unistocks/localization';
import {
  portfoliosGetQueryKey,
  portfoliosGetTimeSeriesQueryKey,
  usePortfoliosGetQuery
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import { getDateRange } from '@unistocks/shared/utils/date-utils';
import { useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';

const PortfolioLineChart = React.lazy(
  () => import('@unistocks/features/portfolios/components/charts/line-chart/line-chart')
);

export const PortfolioHistory: React.FC<DashboardItemProps> = ({
  item,
  openSettings,
  ...frameProps
}) => {
  const { settings } = item;
  const { portfolios, period, portfolioComparisonSettings } = settings;

  const router = useRouter();
  const [[start, end], setDateRange] = useState(getDateRange(period!));

  const { data: portfolio, isSuccess: isSuccessPortfolio } = usePortfoliosGetQuery(portfolios![0]);

  const {
    data: portfolioHistory,
    isFetching: isFetchingHistory,
    isSuccess: isSuccessHistory
  } = usePortfolioHistory(portfolios![0], start, end);

  const title = settings.title?.trim() || portfolio?.name;

  const navigateToPortfolio = useCallback(
    () =>
      router.navigate({
        pathname: '/(tabs)/portfolios/[id]',
        params: { id: portfolios![0] }
      }),
    [router, portfolios]
  );

  return (
    <DashboardItemFrame {...frameProps}>
      <DashboardItemHeader
        item={item}
        title={title}
        openSettings={openSettings}
        onPress={navigateToPortfolio}
      />
      <QueryState
        queryKeys={[
          portfoliosGetQueryKey(portfolios![0]),
          portfoliosGetTimeSeriesQueryKey({
            id: portfolios![0],
            start: start.startOf('year').toISODate(),
            end: end.endOf('year').toISODate(),
            includeTransactions: true
          })
        ]}
        size={'$5'}
        flex={1}
        py={0}
        renderLoading={<PortfolioHistoryLoader />}
        isEmpty={isSuccessHistory && !portfolioHistory.entries.length}
        noDataIcon={LineChart}
        noDataText={t('::Portfolio:LineChart:NoData')}
      >
        {isSuccessPortfolio && isSuccessHistory ? (
          <React.Suspense fallback={<PortfolioHistoryLoader />}>
            <PortfolioLineChart
              portfolio={portfolio!}
              portfolioHistory={portfolioHistory}
              isLoading={isFetchingHistory}
              onDateRangeChange={setDateRange}
              defaultIndex={portfolioComparisonSettings?.index ?? undefined}
              defaultSharedPortfolioId={portfolioComparisonSettings?.sharedPortfolioId ?? undefined}
              onComparisonIndexChange={noop}
              onComparisonSharedPortfolioChange={noop}
              containerProps={{ '$platform-web': { flex: 1 } }}
              chartContainerProps={{ h: 400, '$platform-web': { flex: 1 } }}
              showChartSettingsMenu={false}
              minimizedDatePeriod
            />
          </React.Suspense>
        ) : null}
      </QueryState>
    </DashboardItemFrame>
  );
};

const PortfolioHistoryLoader = () => (
  <Loader size={'$5'} textProps={{ size: '$6' }}>
    {t('AbpUi::LoadingWithThreeDot')}
  </Loader>
);
